<script setup lang="ts">
import { Components } from '@/api/generated/api';
import { SchoolClassType } from '@/api/generated/api.enum';
import { useSchoolClassAccess } from '@/composables/schoolClassAccess/useSchoolClassAccess';
import { useToast } from '@/composables/useToast';
import DialogBase from '@/core/dialogs/base-dialog/DialogBase.vue';
import CreateClassTeamForm from '@/core/dialogs/create-class/CreateClassTeamForm.vue';
import CustomButton from '@/core/elements/CustomButton.vue';
import { IUserWithPermissions } from '@/interfaces';
import { provideViewedSchoolClass } from '@/schoolClasses/useSchoolClassType';
import { assertNotNullOrUndefined } from '@/utils/assertion.utils';
import { haveEqualAccessRights } from 'common/utils/access.right.utils';
import { computed, ref, useTemplateRef } from 'vue';

const { upsertAnyAccessRights } = useSchoolClassAccess();
const { editSuccessToast } = useToast();

const emit = defineEmits<{
  'update:users-with-access': [value: IUserWithPermissions[]];
}>();

const dialogRef = useTemplateRef('dialogRef');
const classInternal = ref<Components.Schemas.SchoolClassDto | null>(null);

// the edited class is not necessarily the routed one
provideViewedSchoolClass(() => classInternal.value ?? { type: SchoolClassType.class });
const usersWithAccessInternal = ref<IUserWithPermissions[]>([]);

const selectedUsers = ref<IUserWithPermissions[]>([]);
const availableSchoolSubjectsInternal = ref<Components.Schemas.SchoolSubjectDto[]>([]);
const availableSchoolSubjectGroupsInternal = ref<Components.Schemas.SchoolSubjectGroupDto[]>([]);
const formValid = ref(true);

const show = (params: {
  class: Components.Schemas.SchoolClassDto;
  usersWithAccess: IUserWithPermissions[];
  availableSchoolSubjects: Components.Schemas.SchoolSubjectDto[];
  availableSchoolSubjectGroups: Components.Schemas.SchoolSubjectGroupDto[];
}): void => {
  classInternal.value = params.class;
  usersWithAccessInternal.value = params.usersWithAccess;
  selectedUsers.value = params.usersWithAccess;
  availableSchoolSubjectsInternal.value = params.availableSchoolSubjects;
  availableSchoolSubjectGroupsInternal.value = params.availableSchoolSubjectGroups;
  dialogRef.value?.show();
  formValid.value = true;
};

const handleUpdateUsers = (teamMembers: IUserWithPermissions[]): void => {
  selectedUsers.value = teamMembers;
};

const hasChanges = computed<boolean>(() => !haveEqualAccessRights(usersWithAccessInternal.value, selectedUsers.value));

const handleSubmit = async (): Promise<void> => {
  assertNotNullOrUndefined(classInternal.value);

  await upsertAnyAccessRights(classInternal.value.id, selectedUsers.value);

  emit('update:users-with-access', selectedUsers.value);
  editSuccessToast();
  dialogRef.value?.hide();
};

defineExpose({
  show,
});
</script>

<template>
  <form @submit.prevent="handleSubmit">
    <DialogBase
      ref="dialogRef"
      :title="$t('classes.editClassAccess')"
      fill-height
      show-back-button
      @click:back="dialogRef?.hide"
    >
      <CreateClassTeamForm
        ref="teamFormRef"
        class="!px-0"
        :current-team="usersWithAccessInternal"
        :available-school-subjects="availableSchoolSubjectsInternal"
        :available-school-subject-groups="availableSchoolSubjectGroupsInternal"
        user-list-item-type="remove"
        :user-list-item-mode="classInternal?.archivedAt ? 'archive' : 'edit'"
        @update:selected-users="handleUpdateUsers"
        @update:is-valid="formValid = $event"
      />

      <template #footer>
        <CustomButton class="ml-auto" :disabled="!hasChanges || !formValid" type="submit">
          {{ $t('general.done') }}
        </CustomButton>
      </template>
    </DialogBase>
  </form>
</template>
